import { ApiError, type components } from '@multaqa/api-client'
import { queryOptions, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { fortifyPost } from './auth-fortify'

export type { LoginResponse, TwoFactorQrCode, TwoFactorRecoveryCodes } from './auth-fortify'
export {
  confirmedPasswordStatus,
  confirmPassword,
  confirmTwoFactorAuthentication,
  enableTwoFactor,
  ensurePasswordConfirmed,
  firstFieldError,
  forgotPassword,
  login,
  resetPassword,
  twoFactorQrCode,
  twoFactorRecoveryCodes,
  updatePassword,
  verifyTwoFactor,
} from './auth-fortify'

type GeneratedCurrentUser = components['schemas']['UserResource']

/**
 * The API always publishes the avatar/email fields. Keeping these optional at
 * the app boundary lets older test fixtures and cached sessions hydrate while
 * the generated contract remains authoritative for fresh responses.
 */
export type CurrentUser = Omit<
  GeneratedCurrentUser,
  | 'avatar_url'
  | 'avatar_url_large'
  | 'pending_email'
  | 'login_phone'
  | 'timezone'
  | 'time_format'
  | 'shift_reminder_minutes'
> &
  Partial<
    Pick<GeneratedCurrentUser, 'avatar_url' | 'avatar_url_large' | 'pending_email' | 'login_phone'>
  > & {
    timezone?: string | null
    time_format?: string
    shift_reminder_minutes?: number
  }

/**
 * The authenticated user, as server state.
 *
 * `retry: false` matters: an unauthenticated visitor gets a 401, and that is a
 * definitive answer, not a transient failure. Retrying it delays every redirect
 * to the login page by the length of the backoff.
 */
export const currentUserQuery = queryOptions({
  queryKey: ['current-user'],
  retry: false,
  staleTime: 5 * 60_000,
  queryFn: async (): Promise<CurrentUser> => {
    const { data } = await api.GET('/user')

    if (!data) {
      throw new Error('GET /user returned no body')
    }

    return data.data
  },
})

/**
 * The base URL for direct Fortify calls.
 *
 * Fortify's auth routes sit on the `web` middleware group, not the `api`
 * group, so they are intentionally not in the api-client's typed paths and
 * the client never sees them. We hit them with raw `fetch` so the cookie
 * is first-party and the CSRF dance works through the same api-client
 * helper that protects every other mutation.
 */
export function useLogout() {
  const queryClient = useQueryClient()
  return async (): Promise<void> => {
    await fortifyPost<void>('/logout', {})
    queryClient.clear()
  }
}

export { ApiError }
